/* ==========================================================================
   Bauer & Meyer — theme.js
   Small, dependency-free web components. Page-specific behaviour (product page)
   lives in product.js and is only loaded by the product section.
   ========================================================================== */

(() => {
  const theme = window.theme || (window.theme = {});

  /* ---------- Utilities ---------- */
  theme.utils = {
    debounce(fn, wait = 250) {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
    },
    fetchConfig(type = 'json', body) {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/${type}`, 'X-Requested-With': 'XMLHttpRequest' },
        body: body ? JSON.stringify(body) : undefined,
      };
    },
    formatMoney(cents) {
      return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR' }).format(cents / 100);
    },
    parseHTML(html) {
      return new DOMParser().parseFromString(html, 'text/html');
    },
    focusable(container) {
      return Array.from(container.querySelectorAll('summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), input:not([type=hidden]):enabled, select:enabled, textarea:enabled, iframe'))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
    },
  };

  /* ---------- Scroll lock + overlay (shared by all drawers) ---------- */
  const overlay = () => document.querySelector('[data-overlay]');
  let openDrawers = 0;
  theme.lockScroll = (lock) => {
    openDrawers = Math.max(0, openDrawers + (lock ? 1 : -1));
    document.body.classList.toggle('is-locked', openDrawers > 0);
    const o = overlay();
    if (o) o.hidden = openDrawers === 0;
  };

  /* ---------- <drawer-panel> ---------- */
  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('.drawer');
      this.addEventListener('keyup', (e) => e.key === 'Escape' && this.close());
      this.addEventListener('keydown', (e) => this.trapFocus(e));
      // Delegated so buttons inside re-rendered regions keep working
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
    }
    get isOpen() { return this.drawer && this.drawer.hasAttribute('open'); }
    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.drawer.setAttribute('open', '');
      this.drawer.setAttribute('aria-hidden', 'false');
      theme.lockScroll(true);
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        const first = this.querySelector('[autofocus]') || theme.utils.focusable(this.drawer)[0];
        if (first) first.focus({ preventScroll: true });
      });
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close() {
      if (!this.isOpen) return;
      this.drawer.removeAttribute('open');
      this.drawer.setAttribute('aria-hidden', 'true');
      theme.lockScroll(false);
      if (this.trigger && this.trigger.setAttribute) {
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.focus({ preventScroll: true });
      }
    }
    trapFocus(e) {
      if (e.key !== 'Tab' || !this.isOpen) return;
      const els = theme.utils.focusable(this.drawer);
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  customElements.define('drawer-panel', DrawerPanel);

  // Any element with data-drawer-open="<id>" opens <drawer-panel id="<id>">
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const panel = document.getElementById(trigger.dataset.drawerOpen);
    if (!panel || typeof panel.open !== 'function') return;
    e.preventDefault();
    panel.open(trigger);
  });
  document.addEventListener('click', (e) => {
    if (!e.target.matches('[data-overlay]')) return;
    document.querySelectorAll('drawer-panel').forEach((d) => d.close());
  });

  /* ---------- Cart API ---------- */
  theme.cart = {
    sectionsToRender() {
      const ids = new Set(['cart-drawer']);
      document.querySelectorAll('[data-cart-section]').forEach((el) => ids.add(el.dataset.cartSection));
      return Array.from(ids);
    },
    async add(formData) {
      formData.append('sections', this.sectionsToRender().join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      this.render(data.sections);
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${theme.routes.cartChange}.js`, theme.utils.fetchConfig('json', { line, quantity, sections: this.sectionsToRender(), sections_url: window.location.pathname }));
      const data = await res.json();
      if (data.errors || data.status) throw new Error(data.errors || data.description || theme.strings.cartError);
      this.render(data.sections);
      return data;
    },
    async addVariant(id, quantity = 1) {
      const fd = new FormData();
      fd.append('id', id);
      fd.append('quantity', quantity);
      return this.add(fd);
    },
    render(sections) {
      if (!sections) return;
      // Remember whether free shipping was already reached, so the truck → check animation plays only once
      const wasReached = !!document.querySelector('.goal__icon.is-reached');
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = theme.utils.parseHTML(html);
        // Every re-renderable region is marked with data-cart-region="<name>" inside its section
        doc.querySelectorAll('[data-cart-region]').forEach((fresh) => {
          document.querySelectorAll(`[data-cart-region="${fresh.dataset.cartRegion}"]`).forEach((current) => {
            current.replaceWith(fresh.cloneNode(true));
          });
        });
      });
      if (!wasReached) document.querySelectorAll('.goal__icon.is-reached').forEach((icon) => icon.classList.add('just-reached'));
      const countEl = document.querySelector('[data-cart-region="drawer-body"] [data-item-count]');
      const count = countEl ? Number(countEl.dataset.itemCount) : null;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
    },
  };

  /* ---------- <cart-count> ---------- */
  class CartCount extends HTMLElement {
    connectedCallback() {
      this.handler = (e) => {
        if (e.detail.count === null) return;
        this.textContent = e.detail.count;
        this.hidden = e.detail.count === 0;
      };
      document.addEventListener('cart:updated', this.handler);
    }
    disconnectedCallback() { document.removeEventListener('cart:updated', this.handler); }
  }
  customElements.define('cart-count', CartCount);

  /* ---------- <cart-drawer> ---------- */
  class CartDrawer extends DrawerPanel {
    connectedCallback() {
      super.connectedCallback();
      document.addEventListener('cart:updated', () => this.syncGift());
    }
    // Optional: auto-add/remove a gift variant when the cart crosses a threshold.
    async syncGift() {
      const body = this.querySelector('[data-cart-region="drawer-body"]');
      if (!body || !body.dataset.giftVariant || this.syncing) return;
      const threshold = Number(body.dataset.giftThreshold);
      const subtotal = Number(body.dataset.subtotalWithoutGift);
      const giftLine = Number(body.dataset.giftLine || 0);
      this.syncing = true;
      try {
        if (subtotal >= threshold && !giftLine) await theme.cart.addVariant(body.dataset.giftVariant, 1);
        else if (subtotal < threshold && giftLine) await theme.cart.change(giftLine, 0);
      } catch (e) { /* gift sync is best-effort */ }
      this.syncing = false;
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ---------- <cart-items>: quantity + remove for drawer and cart page ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.onChange = theme.utils.debounce((input) => this.update(Number(input.dataset.line), Number(input.value)), 350);
      this.addEventListener('change', (e) => { if (e.target.matches('[data-line]')) this.onChange(e.target); });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-remove-line]');
        if (remove) { e.preventDefault(); this.update(Number(remove.dataset.removeLine), 0); }
      });
    }
    async update(line, quantity) {
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      try { await theme.cart.change(line, quantity); }
      catch (err) {
        const msg = this.querySelector('[data-cart-error]');
        if (msg) { msg.textContent = err.message; msg.hidden = false; }
      }
      this.classList.remove('is-loading');
      this.removeAttribute('aria-busy');
    }
  }
  customElements.define('cart-items', CartItems);

  // Cart upgrade tip: swap a single product for its set (same quantity)
  document.addEventListener('click', async (e) => {
    const button = e.target.closest('[data-upgrade-line]');
    if (!button) return;
    button.disabled = true;
    try {
      await theme.cart.change(Number(button.dataset.upgradeLine), 0);
      await theme.cart.addVariant(button.dataset.upgradeVariant, Number(button.dataset.upgradeQuantity) || 1);
    } catch (err) {
      button.disabled = false;
      const msg = document.querySelector('[data-cart-error]');
      if (msg) { msg.textContent = err.message; msg.hidden = false; }
    }
  });

  /* ---------- <quantity-input> ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button[name]').forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        const prev = this.input.value;
        b.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- <product-form>: AJAX add to cart ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      if (theme.cartType === 'page') return;
      e.preventDefault();
      // e.submitter: the sticky add-to-cart bar submits this form from outside it
      const button = (e.submitter && e.submitter.type === 'submit' ? e.submitter : null) || this.form.querySelector('[type="submit"]');
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      const error = this.querySelector('[data-form-error]');
      if (error) error.hidden = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
      try {
        await theme.cart.add(new FormData(this.form));
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.open(button);
        this.dispatchEvent(new CustomEvent('product:added', { bubbles: true }));
      } catch (err) {
        if (error) { error.textContent = err.message; error.hidden = false; }
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- <slider-carousel>: scroll-snap with buttons + progress ---------- */
  class SliderCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.carousel__track');
      if (!this.track) return;
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.bar = this.querySelector('.carousel__progress');
      this.prev && this.prev.addEventListener('click', () => this.go(-1));
      this.next && this.next.addEventListener('click', () => this.go(1));
      this.addEventListener('click', (e) => {
        const dot = e.target.closest('[data-carousel-dot]');
        if (dot && dot.closest('slider-carousel') === this) this.goTo(Number(dot.dataset.carouselDot) || 0);
      });
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', () => requestAnimationFrame(this.update), { passive: true });
      new ResizeObserver(this.update).observe(this.track);
      if (this.dataset.autoplay) this.autoplay(Number(this.dataset.autoplay) * 1000);
    }
    step() {
      const slide = this.track.children[0];
      if (!slide) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }
    go(dir) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const loop = this.hasAttribute('data-loop');
      let target = this.track.scrollLeft + dir * this.step();
      if (loop && dir > 0 && this.track.scrollLeft >= max - 4) target = 0;
      if (loop && dir < 0 && this.track.scrollLeft <= 4) target = max;
      this.track.scrollTo({ left: target, behavior: 'smooth' });
    }
    goTo(index) {
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: index * this.step(), behavior: smooth ? 'smooth' : 'auto' });
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const scrollable = max > 4;
      this.classList.toggle('is-static', !scrollable);
      if (this.prev && !this.hasAttribute('data-loop')) this.prev.disabled = this.track.scrollLeft <= 4;
      if (this.next && !this.hasAttribute('data-loop')) this.next.disabled = this.track.scrollLeft >= max - 4;
      if (this.bar) {
        const visible = this.track.clientWidth / this.track.scrollWidth;
        const pct = max > 0 ? this.track.scrollLeft / max : 0;
        this.bar.style.setProperty('--progress-width', `${Math.min(100, visible * 100)}%`);
        this.bar.style.setProperty('--progress-offset', `${pct * ((1 / visible) - 1) * 100}%`);
        this.bar.hidden = !scrollable;
      }
      const index = Math.round(this.track.scrollLeft / this.step());
      this.querySelectorAll('[data-carousel-dot]').forEach((d, i) => d.setAttribute('aria-current', i === index ? 'true' : 'false'));
    }
    autoplay(ms) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      let timer;
      const start = () => { stop(); timer = setInterval(() => this.go(1), ms); };
      const stop = () => clearInterval(timer);
      this.setAttribute('data-loop', '');
      this.addEventListener('pointerenter', stop);
      this.addEventListener('pointerleave', start);
      this.addEventListener('focusin', stop);
      start();
    }
  }
  customElements.define('slider-carousel', SliderCarousel);

  /* ---------- <tabs-component> ---------- */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(next);
            this.tabs[next].focus();
          }
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', selected);
        tab.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
    }
  }
  customElements.define('tabs-component', TabsComponent);

  /* ---------- <countdown-timer data-end="ISO date"> ---------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.end = new Date(this.dataset.end).getTime();
      if (Number.isNaN(this.end)) return;
      this.parts = { d: this.querySelector('[data-d]'), h: this.querySelector('[data-h]'), m: this.querySelector('[data-m]'), s: this.querySelector('[data-s]') };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const diff = Math.max(0, this.end - Date.now());
      const pad = (n) => String(n).padStart(2, '0');
      const d = Math.floor(diff / 864e5), h = Math.floor(diff / 36e5) % 24, m = Math.floor(diff / 6e4) % 60, s = Math.floor(diff / 1e3) % 60;
      if (this.parts.d) this.parts.d.textContent = d;
      if (this.parts.h) this.parts.h.textContent = pad(h);
      if (this.parts.m) this.parts.m.textContent = pad(m);
      if (this.parts.s) this.parts.s.textContent = pad(s);
      if (diff === 0) {
        clearInterval(this.timer);
        if (this.dataset.hideOnEnd === 'true') this.closest('[data-countdown-wrapper]')?.setAttribute('hidden', '');
      }
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ---------- <delivery-estimate>: order cut-off countdown + concrete delivery date ----------
     Ships the same workday when ordered before the cut-off (Mon–Fri, minus closed dates) and arrives the
     next delivery day (Mon–Sat). Templates: data-text-countdown "… [time] … [date]", data-text-later "… [date]". */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.render();
      this.timer = setInterval(() => this.render(), 30000);
    }

    disconnectedCallback() { clearInterval(this.timer); }

    render() {
      const parts = {};
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(new Date()).forEach((p) => { parts[p.type] = p.value; });

      const DAY = 864e5;
      const [cutH, cutM] = (this.dataset.cutoff || '15:00').split(':').map(Number);
      const closed = (this.dataset.closed || '').split(',').map((d) => d.trim()).filter(Boolean);
      const isOpen = (d) => !closed.includes(d.toISOString().slice(0, 10));
      const next = (d) => new Date(d.getTime() + DAY);

      const today = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
      const minutesLeft = cutH * 60 + (cutM || 0) - (Number(parts.hour) * 60 + Number(parts.minute));
      const isWorkday = (d) => d.getUTCDay() >= 1 && d.getUTCDay() <= 5 && isOpen(d);

      let ship = today;
      if (!isWorkday(today) || minutesLeft <= 0) {
        do { ship = next(ship); } while (!isWorkday(ship));
      }
      let arrival = next(ship);
      while (arrival.getUTCDay() === 0 || !isOpen(arrival)) arrival = next(arrival);

      const fmt = (o) => new Intl.DateTimeFormat('nl-NL', Object.assign({ timeZone: 'UTC' }, o)).format(arrival);
      const days = Math.round((arrival.getTime() - today.getTime()) / DAY);
      const date = days === 1 ? `morgen (${fmt({ weekday: 'short', day: 'numeric', month: 'short' })})` : fmt({ weekday: 'long', day: 'numeric', month: 'long' });

      let template = this.dataset.textLater || '[date]';
      let time = '';
      if (ship === today && this.dataset.textCountdown) {
        const h = Math.floor(minutesLeft / 60);
        const m = minutesLeft % 60;
        time = h > 0 ? (m > 0 ? `${h} uur ${m} min` : `${h} uur`) : `${m} min`;
        template = this.dataset.textCountdown;
      }

      // Build safely: text with <strong> for the time and the date
      const target = this.querySelector('[data-delivery-text]') || this;
      target.textContent = '';
      template.split(/(\[time\]|\[date\])/).forEach((piece) => {
        if (piece === '[time]' || piece === '[date]') {
          const strong = document.createElement('strong');
          strong.textContent = piece === '[time]' ? time : date;
          target.append(strong);
        } else if (piece) {
          target.append(piece);
        }
      });
      this.hidden = false;
    }
  }
  if (!customElements.get('delivery-estimate')) customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- <predictive-search> ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      this.idle = this.querySelector('[data-search-idle]');
      if (!this.input) return;
      this.input.addEventListener('input', theme.utils.debounce(() => this.search(), 280));
      this.querySelectorAll('[data-search-term]').forEach((chip) => chip.addEventListener('click', () => {
        this.input.value = chip.dataset.searchTerm;
        this.search();
        this.input.focus();
      }));
    }
    async search() {
      const q = this.input.value.trim();
      if (this.controller) this.controller.abort();
      if (!q) { this.results.innerHTML = ''; if (this.idle) this.idle.hidden = false; return; }
      this.controller = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.controller.signal });
        const html = await res.text();
        const doc = theme.utils.parseHTML(html);
        const fresh = doc.querySelector('#predictive-search-results');
        this.results.innerHTML = fresh ? fresh.innerHTML : '';
        if (this.idle) this.idle.hidden = true;
      } catch (e) { /* aborted */ }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- <site-header>: sticky state + header height variable ---------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      const setHeight = () => document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      new ResizeObserver(setHeight).observe(this);
      setHeight();
      this.fitNav();
      if (this.dataset.sticky !== 'true') return;
      const sentinel = document.createElement('div');
      sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;';
      this.parentElement.before(sentinel);
      new IntersectionObserver(([entry]) => this.classList.toggle('is-scrolled', !entry.isIntersecting)).observe(sentinel);

      // Desktop mega menus: open on hover, close on leave / Escape
      this.querySelectorAll('details[data-hover]').forEach((details) => {
        const summary = details.querySelector('summary');
        let t;
        const open = () => { clearTimeout(t); this.closeOthers(details); details.open = true; summary.setAttribute('aria-expanded', 'true'); };
        const close = () => { t = setTimeout(() => { details.open = false; summary.setAttribute('aria-expanded', 'false'); }, 120); };
        details.addEventListener('mouseenter', () => window.matchMedia('(hover: hover)').matches && open());
        details.addEventListener('mouseleave', () => window.matchMedia('(hover: hover)').matches && close());
        details.addEventListener('toggle', () => { summary.setAttribute('aria-expanded', details.open); if (details.open) this.closeOthers(details); });
        details.addEventListener('keyup', (e) => { if (e.key === 'Escape') { details.open = false; summary.focus(); } });
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closeOthers(); });
    }
    // Switch to the burger menu when the desktop nav doesn't fit next to the centred logo
    fitNav() {
      const menu = this.querySelector('.header__menu');
      const left = this.querySelector('.header__left');
      if (!menu || !left) return;
      const measure = () => {
        this.classList.remove('header--compact');
        const fits = menu.scrollWidth <= left.clientWidth;
        this.classList.toggle('header--compact', !fits);
      };
      new ResizeObserver(theme.utils.debounce(measure, 100)).observe(this);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
      measure();
    }
    closeOthers(except) {
      this.querySelectorAll('details[data-hover][open]').forEach((d) => { if (d !== except) d.open = false; });
    }
  }
  customElements.define('site-header', SiteHeader);

  /* ---------- Reveal on scroll ---------- */
  const reveal = () => {
    const els = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
    }), { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
  };
  document.addEventListener('DOMContentLoaded', reveal);
  document.addEventListener('shopify:section:load', reveal);

  /* ---------- Contact form spam guard (snippets/form-spam-guard.liquid) ---------- */
  // A real keystroke or tap in the form marks it as sent by a person
  const markHuman = (e) => {
    if (!(e.target instanceof Element) || e.target.matches('[data-spam-trap]')) return;
    const check = e.target.closest('form')?.querySelector('[data-spam-check]');
    if (check) check.value = 'mens';
  };
  document.addEventListener('keydown', markHuman, true);
  document.addEventListener('pointerdown', markHuman, true);
  // Capture phase, so a blocked submit never reaches Shopify's hCaptcha handler either
  document.addEventListener('submit', (e) => {
    const check = e.target.querySelector('[data-spam-check]');
    if (!check) return;
    const trap = e.target.querySelector('[data-spam-trap]');
    if (check.value && !(trap && trap.value)) return;
    e.preventDefault();
    e.stopPropagation();
  }, true);
})();
